import React, { useEffect, useState } from 'react';
import { SectionBand, LISTED_GRAY, MUTED, GREEN_ON_WHITE } from '../proUi';
import { DAYS, DAY_ORDER, hoursRowText, openState, attrLabel, clock12 } from './vendorUi';

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

/**
 * A static Mapbox map of the address. Geocodes the street address once (so the pin lands on the
 * building, not the city centre), falls back to the saved city point, and to a plain placeholder
 * when there's no token or no location at all. Clicking opens directions.
 */
export function AddressMap({ biz, href, width = 200, height = 160, fluid = false, radius = 0 }) {
  const [pt, setPt] = useState(biz.longitude != null && biz.latitude != null ? [biz.longitude, biz.latitude] : null);
  const query = biz.streetAddress ? `${biz.streetAddress}${(biz.baseCity || biz.listedCity) ? `, ${biz.baseCity || biz.listedCity}` : ''}` : null;
  useEffect(() => {
    let cancel = false;
    if (!MAPBOX_TOKEN || !query) return undefined;
    (async () => {
      try {
        const r = await fetch(`https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?types=address&limit=1&access_token=${MAPBOX_TOKEN}`);
        const d = await r.json();
        const c = d?.features?.[0]?.center;
        if (!cancel && Array.isArray(c) && c.length === 2) setPt(c);
      } catch { /* keep the city fallback */ }
    })();
    return () => { cancel = true; };
  }, [query]);

  const box = fluid ? { width: '100%', height, borderRadius: radius } : { width, height, borderRadius: radius };
  const placeholder = (
    <div className="flex items-center justify-center" style={{ ...box, background: fluid ? '#F2F2F2' : '#F5F5F5' }} aria-hidden="true">
      <span className="font-mono uppercase" style={{ fontSize: 9, letterSpacing: '0.1em', color: LISTED_GRAY }}>MAP</span>
    </div>
  );
  if (!MAPBOX_TOKEN || !pt) return placeholder;
  const [lng, lat] = pt;
  const src = `https://api.mapbox.com/styles/v1/mapbox/light-v11/static/pin-s+000000(${lng},${lat})/${lng},${lat},14,0/${width}x${height}@2x?access_token=${MAPBOX_TOKEN}`;
  const img = <img src={src} alt="Map" width={width} height={height} style={{ display: 'block', objectFit: 'cover', ...box }} />;
  return href ? <a href={href} target="_blank" rel="noreferrer" style={{ display: 'block', lineHeight: 0 }}>{img}</a> : img;
}

/**
 * HOURS · WHERE · ATTRIBUTES — shared by the four vendor kinds, never on a bookable page
 * (Vendor Public Pages §3). Listed pages: pill reads AS POSTED, no today highlight, grey chips.
 */
export default function PlaceInfo({ biz, mobile = false, only = null, onOutbound }) {
  const listed = !!biz.isListed;
  const hours = biz.hours || [];
  const state = listed ? null : openState(hours);
  const today = new Date().getDay();
  const border = listed ? LISTED_GRAY : '#000';
  const showHours = !only || only === 'hours';
  const showWhere = !only || only === 'where';
  const showAttrs = !only || only === 'attributes';
  const hasWhere = biz.streetAddress || biz.neighbourhood || biz.parkingNote || biz.baseCity || biz.listedCity;
  const attrs = biz.attributes || [];
  const mapsHref = biz.streetAddress ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${biz.streetAddress}${biz.baseCity ? `, ${biz.baseCity}` : ''}`)}` : null;

  const pill = (
    <span className="font-mono font-bold uppercase" style={{ fontSize: 10, letterSpacing: '0.1em', padding: '4px 7px', border: `2px solid ${listed ? LISTED_GRAY : '#000'}`, whiteSpace: 'nowrap',
      background: listed ? '#fff' : state?.open ? GREEN_ON_WHITE : LISTED_GRAY, color: listed ? LISTED_GRAY : state?.open ? '#fff' : '#000' }} data-testid="hours-pill">
      {listed ? 'AS POSTED' : state ? (state.open ? state.label : (state.label.startsWith('CLOSED') ? state.label : 'CLOSED')) : 'HOURS NOT SET'}
    </span>
  );

  const hoursCard = hours.length > 0 && (
    <div className="flex flex-col bg-white" style={{ border: `2px solid ${border}`, flex: mobile ? undefined : '0 0 340px' }} data-testid="hours-card">
      <div className="flex items-baseline justify-between gap-2" style={{ padding: '11px 16px', borderBottom: `2px solid ${border}` }}>
        <span className="font-mono font-bold uppercase" style={{ fontSize: 11, letterSpacing: '0.1em', color: listed ? MUTED : '#000' }}>HOURS</span>
        {pill}
      </div>
      {DAY_ORDER.map((d, i) => {
        const h = hours.find((x) => x.dayOfWeek === d);
        const isToday = !listed && d === today;
        const text = hoursRowText(h);
        const closed = /closed/i.test(text);
        return (
          <div key={d} className="flex items-center justify-between gap-2" style={{ padding: '7px 16px', borderTop: i === 0 ? 'none' : `2px solid ${border}`, background: isToday ? '#F5F5F5' : '#fff' }}>
            <span className="font-mono uppercase" style={{ fontSize: 11, letterSpacing: '0.05em', fontWeight: isToday ? 700 : 400, color: isToday ? '#000' : MUTED }}>{DAYS[d]}</span>
            <span className="font-mono" style={{ fontSize: 12, letterSpacing: '0.02em', fontWeight: isToday ? 700 : 400, color: closed ? LISTED_GRAY : isToday ? '#000' : '#1A1A1A' }}>{text}</span>
          </div>
        );
      })}
      {hours.some((h) => h.kitchenCloses && !h.isClosed) && (
        <div className="font-mono" style={{ borderTop: `2px solid ${border}`, padding: '9px 16px', fontSize: 11, letterSpacing: '0.05em', color: MUTED }}>
          Kitchen closes {clock12(hours.find((h) => h.kitchenCloses && !h.isClosed).kitchenCloses)}.
        </div>
      )}
    </div>
  );

  const whereCard = hasWhere && (
    <div className="flex flex-1 flex-col bg-white" style={{ border: `2px solid ${border}` }} data-testid="where-card">
      <div style={{ padding: '11px 16px', borderBottom: `2px solid ${border}` }}>
        <span className="font-mono font-bold uppercase" style={{ fontSize: 11, letterSpacing: '0.1em', color: listed ? MUTED : '#000' }}>WHERE</span>
      </div>
      <div className="flex flex-1" style={{ minHeight: 0 }}>
        <div className="flex flex-1 flex-col" style={{ padding: '14px 16px', gap: 8 }}>
          {(biz.streetAddress || biz.baseCity || biz.listedCity) && (
            <div className="font-mono" style={{ fontSize: 14, lineHeight: 1.4, color: '#1A1A1A' }}>{[biz.streetAddress, biz.baseCity || biz.listedCity].filter(Boolean).join(', ')}</div>
          )}
          {biz.neighbourhood && <div className="font-mono uppercase" style={{ fontSize: 11, letterSpacing: '0.05em', color: MUTED }}>{biz.neighbourhood}</div>}
          {biz.parkingNote && <div className="font-mono" style={{ fontSize: 12, lineHeight: 1.5, color: MUTED }}>{biz.parkingNote}</div>}
          {mapsHref && !listed && (
            <a href={mapsHref} target="_blank" rel="noreferrer" onClick={() => onOutbound?.('directions')} className="self-start font-mono font-bold uppercase underline hover:opacity-80" style={{ fontSize: 11, letterSpacing: '0.05em' }}>DIRECTIONS ↗</a>
          )}
        </div>
        {!mobile && (
          <div className="flex-none overflow-hidden" style={{ flex: '0 0 200px', borderLeft: `2px solid ${border}` }} data-testid="where-map">
            <AddressMap biz={biz} href={listed ? null : mapsHref} width={200} height={180} />
          </div>
        )}
      </div>
    </div>
  );

  const attrsCard = attrs.length > 0 && (
    <div className="bg-white" style={{ border: `2px solid ${border}` }} data-testid="attributes-card">
      <div style={{ padding: '11px 16px', borderBottom: `2px solid ${border}` }}>
        <span className="font-mono font-bold uppercase" style={{ fontSize: 11, letterSpacing: '0.1em', color: listed ? MUTED : '#000' }}>ATTRIBUTES</span>
      </div>
      <div className="flex flex-wrap" style={{ padding: '14px 16px', gap: 8 }}>
        {attrs.map((a) => (
          <span key={a} className="font-mono font-bold uppercase bg-white" style={{ fontSize: 11, letterSpacing: '0.05em', padding: '7px 10px', border: `2px solid ${listed ? LISTED_GRAY : '#000'}`, color: listed ? MUTED : '#000', whiteSpace: 'nowrap' }}>{attrLabel(a)}</span>
        ))}
      </div>
    </div>
  );

  if (!hoursCard && !whereCard && !attrsCard) return null;
  if (only) return <div className="flex flex-col" style={{ gap: 16 }}>{showHours && hoursCard}{showWhere && whereCard}{showAttrs && attrsCard}</div>;
  return (
    <div className={`flex ${mobile ? 'flex-col' : 'items-stretch'}`} style={{ gap: 16 }} data-testid="place-info">
      {hoursCard}
      <div className="flex flex-1 flex-col" style={{ gap: 16, minWidth: 0 }}>
        {whereCard}
        {attrsCard}
      </div>
    </div>
  );
}

// Re-exported for the header's trust line and the directory card.
export { SectionBand };
